import { RadioParameters } from '../types/systems';

export type RadioBudgetField =
	| 'txPowerDbm'
	| 'txGainDbi'
	| 'rxGainDbi'
	| 'systemLossDb'
	| 'fadingMarginDb'
	| 'receiverSensitivityDbm'
	| 'thresholdDb';

export type RadioParameterLimit = {
	label: string;
	unit: string;
	min: number;
	max: number;
	precision: number;
	step: number;
};

export const RADIO_BUDGET_FIELDS: RadioBudgetField[] = [
	'txPowerDbm',
	'txGainDbi',
	'rxGainDbi',
	'systemLossDb',
	'fadingMarginDb',
	'receiverSensitivityDbm',
	'thresholdDb',
];

// Broad application guardrails, not regulatory limits. Legal transmitter limits
// depend on frequency, service and location and must be checked separately.
export const RADIO_PARAMETER_LIMITS: Record<RadioBudgetField, RadioParameterLimit> = {
	txPowerDbm: {
		label: 'Transmit power',
		unit: 'dBm',
		min: 0,
		max: 80,
		precision: 1,
		step: 1,
	},
	txGainDbi: {
		label: 'Transmit antenna gain',
		unit: 'dBi',
		min: -50,
		max: 100,
		precision: 1,
		step: 0.5,
	},
	rxGainDbi: {
		label: 'Receive antenna gain',
		unit: 'dBi',
		min: -50,
		max: 100,
		precision: 1,
		step: 0.5,
	},
	systemLossDb: {
		label: 'System losses',
		unit: 'dB',
		min: 0,
		max: 100,
		precision: 1,
		step: 0.5,
	},
	fadingMarginDb: {
		label: 'Fade margin',
		unit: 'dB',
		min: 0,
		max: 100,
		precision: 1,
		step: 0.5,
	},
	receiverSensitivityDbm: {
		label: 'Receiver sensitivity',
		unit: 'dBm',
		min: -200,
		max: 0,
		precision: 1,
		step: 1,
	},
	thresholdDb: {
		label: 'Required link margin',
		unit: 'dB',
		min: 0,
		max: 100,
		precision: 1,
		step: 0.5,
	},
};

export const dbmToWatts = (dbm: number) => 10 ** ((dbm - 30) / 10);

export const wattsToDbm = (watts: number) => 10 * Math.log10(watts) + 30;

export const TRANSMIT_POWER_WATTS_LIMITS = {
	min: dbmToWatts(RADIO_PARAMETER_LIMITS.txPowerDbm.min),
	max: dbmToWatts(RADIO_PARAMETER_LIMITS.txPowerDbm.max),
	precision: 6,
	step: 0.1,
};

export const isRadioParameterInRange = (
	field: RadioBudgetField,
	value: RadioParameters[RadioBudgetField]
) => {
	if (typeof value !== 'number' || !Number.isFinite(value)) return false;
	const { min, max } = RADIO_PARAMETER_LIMITS[field];
	return value >= min && value <= max;
};
